<template>
  <div class="project">

    <nav class="navbar is-mobile is-medium" style="background-color: #30008A;" role="navigation" aria-label="main navigation">
      <div class="navbar-brand">
        <img id="navbar-logo" src="../../assets/projects/smartmirror/smartmirror.jpg" alt="SmartMobile Logo">
      </div>
    </nav>



    <!-- Title -->
    <h1 class="title" id="top"> Exploration project - “Smart Mirror”</h1>
    
    <p>
      In one of my initial undertakings, a friend and I conceptualized a smart mir- ror project. This innovative creation entailed overlaying a screen layer onto a conventional mirror. The primary objective was to design a device that seamlessly synchronized with your morning routine, delivering pertinent information.
      Imagine engaging in your morning rituals, and the mirror automatically rec- ognizes you through facial recognition as you brush your teeth. A simple swipe to the left grants you access to the day’s weather forecast, while a swipe to the right offers insights into the optimal public transport options to ensure punctual arrival at work.
      For this endeavor, we employed a blend of HTML, CSS, and Javascript for the frontend, coupled with Python for backend functionalities. This venture symbolized not only our technical acumen but also our aspiration to seam- lessly integrate technology into daily life routines.
    </p>

<br>
        <div class="columns">
          <div class="column">
            <img src="../../assets/projects/smartmirror/IMG_20190214_173624.jpg" alt="SmartMobile">
          </div>
          <div class="column">
            <img src="../../assets/projects/smartmirror/IMG_20190214_172616.jpg" alt="SmartMobile">
          </div>
        </div>


    <!-- Introduction -->
    <p>
      In this document, I will write my experiences and vision on speech recognition, what I tried to build with it, and why I made certain decisions.
    </p>
    
    <!-- Table of Contents -->
    <h2>Table of Contents</h2>
    <ul>
      <li><a href="#library">Library (https://github.com/Uberi/speech_recognition)</a></li>
      <li><a href="#hardware">Hardware</a></li>
      <li><a href="#software-requirements">Software Requirements</a></li>
      <li><a href="#operators-matrix">Operators Contribution Matrix</a></li>
      <li><a href="#smart-mirror">Smart Mirror and SpeechRecognition</a></li>
      <li><a href="#system-drawing">System Drawing</a></li>
      <li><a href="#installation-guide">Installation Guide (Mac)</a></li>
      <li><a href="#process-experiences">Process, Experiences, and Challenges</a></li>
      <li><a href="#acceptation-test">Acceptation Test</a></li>
    </ul>
    
    <!-- Library -->
    <section id="library" class="library-section">
      <h2>Library (https://github.com/Uberi/speech_recognition)</h2>
      <p>
        The library is a very comprehensive piece of software with various capabilities, including:
      </p>
      <ul>
        <li>Transcribing an audio file</li>
        <li>Saving audio data to an audio file</li>
        <li>Recognizing speech input from a microphone</li>
        <li>Calibrating the recognizer energy threshold for ambient noise levels</li>
        <li>Listening to a microphone in the background</li>
      </ul>
      <p>
        The library is Python-based and provides helpful examples for ease of understanding.
      </p>
    </section>
    
    <!-- Hardware -->
    <section id="hardware" class="hardware-section">
      <h2>Hardware</h2>
      <p>
        The only hardware requirement is a microphone and a system capable of running Python with a Terminal or IDE for running Python scripts.
      </p>
      <p>
        The software has been tested on various machines, including Davey's MacBook, Vincent's Raspberry Pi, and Vincent's Windows laptop.
      </p>
    </section>
    
    <!-- Software Requirements -->
    <section id="software-requirements" class="software-requirements-section">
      <h2>Software Requirements</h2>
      <p>
        To utilize the library's full functionality, you should have the following software requirements:
      </p>
      <ul>
        <li>Python 2.6, 2.7, or 3.3+</li>
        <li>PyAudio 0.2.11+ (required for microphone input)</li>
        <li>PocketSphinx (for the offline library)</li>
        <li>FLAC encoder (required if the system is not x86-based Windows/Linux/OS X)</li>
      </ul>
    </section>
    
    <!-- Operators Contribution Matrix -->
    <section id="operators-matrix" class="operators-matrix-section">
      <h2>Operators Contribution Matrix</h2>
      <br>
        <div class="columns">
          <div class="column is-full">
            <img src="../../assets/projects/smartmirror/table2.jpg" alt="img">
        </div>
      </div>
      <p>
        I decided to stick with Google Speech because it worked well for me. It is fast and efficient, while the dictionary from Sphinx did not perform optimally.
      </p>
    </section>
    
    <!-- Smart Mirror and SpeechRecognition -->
    <section id="smart-mirror" class="smart-mirror-section">
      <h2>Smart Mirror and SpeechRecognition</h2>
      <p>
        In one of my initial undertakings, a friend and I conceptualized a smart mirror project. This innovative creation entailed overlaying a screen layer onto a conventional mirror. The primary objective was to design a device that seamlessly synchronized with your morning routine, delivering pertinent information.
      </p>
      <p>
        Imagine engaging in your morning rituals, and the mirror automatically recognizes you through facial recognition as you brush your teeth. A simple swipe to the left grants you access to the day’s weather forecast, while a swipe to the right offers insights into the optimal public transport options to ensure punctual arrival at work.
      </p>
      <p>
        For this endeavor, we employed a blend of HTML, CSS, and Javascript for the frontend, coupled with Python for backend functionalities. This venture symbolized not only our technical acumen but also our aspiration to seamlessly integrate technology into daily life routines.
      </p>
    </section>
    
    <!-- System Drawing -->
    <section id="system-drawing" class="system-drawing-section">
      <h2>System Drawing</h2>

      <br>
        <div class="columns">
          <div class="column">
            <img src="../../assets/projects/smartmirror/systemdrawing.jpg" alt="img">
          </div>
        </div> 
    
      
    </section>
    
    <!-- Installation Guide (Mac) -->
    <section id="installation-guide" class="installation-guide-section">
      <h2>Installation Guide (Mac)</h2>
      <p>
        Make sure you have one of the following versions of Python installed:
      </p>
      <ul>
        <li>2.6</li>
        <li>2.7</li>
        <li>3.3 or newer</li>
      </ul>
      <p>
        After that, you can install the following commands in your terminal:
      </p>
      <p>(Note: Replace pip with pip3 if you use python3)</p>
      <p>Speech:</p>
      <ul>
        <li>pip install SpeechRecognition</li>
      </ul>
      <p>PyAudio:</p>
      <ul>
        <li>pip install pyaudio</li>
        <li>sudo apt-get install portaudio19-dev python-all-dev python3-all-dev && sudo pip install pyaudio</li>
      </ul>
      <p>Wheel:</p>
      <ul>
        <li>pip install wheel</li>
        <li>pip install ./third-party/WHEEL_FILENAME</li>
      </ul>
      <p>Flac:</p>
      <ul>
        <li>sudo apt-get install flac</li>
        <li>pip install monotonic</li>
      </ul>
      <p>
        After installing these packages, you should be able to run the Python scripts through your Terminal and IDE if it can run Python.
      </p>
    </section>
    
    <!-- Process, Experiences and Challenges -->
    <section id="process-experiences" class="process-experiences-section">
      <h2>Process, Experiences, and Challenges</h2>
      <p>
        It was very fun to work with speech recognition, and I will experiment more with it in the future. In the later parts of this semester, a new operator showed up, and I am looking forward to experimenting with that one (Snowboy Hotword Detection) and maybe change Google Speech for this one if not possible. I would like to dive deeper into Sphinx and make it so that my NAVO.dict dictionary works in a stable environment.
      </p>
      <p>
        The hardest challenge was to get Sphinx to use a handmade dictionary and make it that it detects it fast and accurate. As said before, I went further with Google Speech because it listened quite well to the NAVO alphabet.
      </p>
      <p>
        Despite not using a different dictionary, I still learned a lot of things like Python and the combination with JavaScript. Before I started this project, I didn't have any knowledge of Python at all.
      </p>
      <p>
        The thing I surprisingly learned more of than I thought I would have was Linux and Mac. I had Linux as an old course, but we never got to stuff like this, and I liked it a lot.
      </p>
    </section>
    
    <!-- Acceptation Test -->
    <section id="acceptation-test" class="acceptation-test-section">
      <h2>Acceptation Test</h2>
      <img src="../../assets/projects/smartmirror/table1.jpg" alt="img">
    </section>
  </div>
</template>

<script>
export default {
  name: 'SpeechRecognitionPage'
}
</script>

<style scoped>
  
@import 'project.css';

</style>